import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SessionExpiredComponent } from 'src/app/shared/components/sessionExpired/sessionExpired/session-expired.component';
import { environment } from '../../../environments/environment';
import { TuwaiqUtil } from '../TuwaiqUtil';
import { ADMISSION_REQUEST_TYPE } from '../constants/admission-request-type.enum';
import { ApiConstants } from '../constants/api.constants';
import { CALL_RECORD_DASHBOARD_BLOCK_TYPES } from '../constants/call-record-dashboard-block-types.enum';
import { DASHBOARD_BLOCK_TYPES } from '../constants/dashboard-block-types.enum';
import { GlobalConstants } from '../constants/globals';
import { TuwaiqConfig } from '../constants/tuwaiq.config';
import { USER_ROLE } from '../constants/user-role.enum';
import { VIEW_TYPE } from '../constants/view-type.enum';
import { RequestDetailsResponse } from '../models/request-details-response.modal';
import { TranslateService } from '../translate';
import { AuthService } from './auth.service';
import { LoggedInUserService } from './logged-in-user.service';
import { ParamHandlerService } from './param-handler.service';

@Injectable({
    providedIn: 'root'
})
export class ApiRequestService {
    isExpiredSession: boolean = false;
    filterItemList = new Subject<any>();
    constructor(
        private http: HttpClient,
        private loggedInUserService: LoggedInUserService,
        private authService: AuthService,
        public dialog: MatDialog,
        private readonly translateService: TranslateService,
        private readonly paramHandlerService: ParamHandlerService
    ) {}

    getRequestList(parameters: HttpParams, viewType?: VIEW_TYPE) {
        const userRole = this.loggedInUserService.getUserRole();

        let api = this.getRequestAPIBasedOnUserRoleAndViewType(userRole, viewType);
        if (userRole === USER_ROLE.ACCOUNT_MANAGER) {
            const clientId = this.loggedInUserService.getLoggedInUserDetails().client.id;
            if (viewType !== VIEW_TYPE.EMPLOYEE_MANAGEMENT_REQUESTS) {
                parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.INCLUDE, TuwaiqConfig.REQUEST_INCLUDES.CREATOR);
            }

            api = api.replace('{clientId}', clientId.toString());
        }
        api = this.handleSectionFilter(api); // to update the section filter
        return this.http.get(api, { params: parameters, observe: 'response' }).pipe(
            map(response => {
                const responseData = response.body;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getRequestAPIBasedOnUserRoleAndViewType(role: USER_ROLE, viewType: VIEW_TYPE): string {
        if (viewType === VIEW_TYPE.SECURITY_COORDINATOR_REQUESTS && (this.loggedInUserService.isSuperAdmin() || this.loggedInUserService.isGeneralManager())) {
            return ApiConstants.ADMISSIONS_FOR_SUPER_ADMIN;
        } else if (viewType === VIEW_TYPE.EMPLOYEE_MANAGEMENT_REQUESTS && role === USER_ROLE.ACCOUNT_MANAGER) {
            return ApiConstants.ADMISSIONS_FOR_ACCOUNT_MANAGER;
        } else if (
            viewType === VIEW_TYPE.TECHNICAL_OPERATIONS_REQUESTS &&
            (this.loggedInUserService.isAdminOrManager() || this.loggedInUserService.isGeneralManager())
        ) {
            return ApiConstants.OPERATION_REQUESTS_FOR_ADMINS_AND_MANAGERS;
        }
        const userRole = [];
        userRole[USER_ROLE.ADMIN] = ApiConstants.ADMIN_REQUESTS_LIST;
        userRole[USER_ROLE.GENERAL_MANAGER] = ApiConstants.ADMIN_REQUESTS_LIST;
        userRole[USER_ROLE.SHIFT_MANAGER] = ApiConstants.MANAGER_REQUESTS_LIST;
        userRole[USER_ROLE.SECTION_MANAGER] = ApiConstants.MANAGER_REQUESTS_LIST;
        userRole[USER_ROLE.EMPLOYEE] = ApiConstants.EMPLOYEE_REQUESTS_LIST;
        userRole[USER_ROLE.REQUESTER] = ApiConstants.REQUESTER_REQUESTS_LIST;
        userRole[USER_ROLE.ACCOUNT_MANAGER] = ApiConstants.ACCOUNT_MANAGER_REQUESTS_LIST;
        userRole[USER_ROLE.OPERATION_SHIFT_MANAGER] = ApiConstants.OPERATION_MANAGER_REQUESTS_LIST;
        userRole[USER_ROLE.OPERATION_EMPLOYEE] = ApiConstants.OPERATION_EMPLOYEE_REQUESTS_LIST;
        return userRole[role];
    }

    employeeSearch(querySearch: string, page: number, sectionId: number) {
        let parameters = new HttpParams();
        let api;
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.PAGE, page);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SIZE, TuwaiqConfig.PAGINATION_CONFIG.EMPLOYEE_SEARCH_DEFAULT_PAGE_SIZE);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, querySearch);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.INCLUDE, TuwaiqConfig.REQUEST_INCLUDES.ASSIGNED_REQUEST_COUNT);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.STATUS, TuwaiqConfig.REQUEST_INCLUDES.AVAILABLE);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SECTION_FILTER, sectionId);
        if (this.loggedInUserService.isAdminOrSuperAdmin()) {
            api = ApiConstants.USER_SEARCH_ADMINS;
        } else if (this.loggedInUserService.isOperationShiftManager()) {
            api = ApiConstants.USER_SEARCH_OPERATIONS;
        } else {
            api = ApiConstants.USER_SEARCH_MANAGERS;
        }

        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    login(email: string) {
        return this.http.post(
            ApiConstants.DEV_LOGIN,
            {
                email,
                password: 'stc123'
            },
            { withCredentials: true }
        );
    }

    assignMultipleRequests(userId, requestIDs, body?) {
        let api, requestBody;
        if (body != '' && body != undefined) {
            requestBody = {
                'user-id': userId,
                'request-ids': requestIDs,
                comment: body?.comment,
                attachments: body?.attachments
            };
        } else {
            requestBody = {
                'user-id': userId,
                'request-ids': requestIDs
            };
        }
        if (this.loggedInUserService.isOperationShiftManager()) {
            api = ApiConstants.ASSIGN_MULTIPLE_REQUESTS_OPERATION;
        } else {
            api = ApiConstants.ASSIGN_MULTIPLE_REQUESTS;
        }
        return this.http.post(api, requestBody).pipe(
            map(response => {
                //since response is null,
                response = response === null ? 'success' : response;
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getClientList() {
        const api = this.getClientListEndPointByRole();
        return this.http.get(api);
    }

    private getClientListEndPointByRole(): string {
        if (this.loggedInUserService.isAdminOrSuperAdmin() || this.loggedInUserService.IsUserRelatedQualityAssurance()) {
            return this.handleSectionFilter(ApiConstants.CLIENT_LIST);
        }
        return ApiConstants.CLIENT_LIST;
    }
    private getClientManagementListEndPointByRole(): string {
        if (this.loggedInUserService.isAdminOrSuperAdmin()) {
            return this.handleSectionFilter(ApiConstants.GET_CLIENT_MANAGEMENT_LIST);
        }
        return ApiConstants.GET_CLIENT_MANAGEMENT_LIST;
    }

    getClientListForManager() {
        const api = this.getClientListForManagerEndPointByRole();
        return this.http.get(api);
    }

    private getClientListForManagerEndPointByRole(): string {
        if (this.loggedInUserService.isSectionManager() || this.loggedInUserService.isShiftManager() || this.loggedInUserService.isEmployee()) {
            return this.replaceSectionId(ApiConstants.CLIENT_LIST_MANAGER_BY_SECTION);
        }
        if (this.loggedInUserService.isAdminOrSuperAdmin()) {
            return this.handleSectionFilter(ApiConstants.CLIENT_LIST_MANAGER);
        }
        if (this.loggedInUserService.isOperationEmployee()) {
            return this.handleSectionFilter(ApiConstants.CLIENT_LIST);
        }
        return ApiConstants.CLIENT_LIST_MANAGER;
    }

    getServiceList() {
        const userRole = this.loggedInUserService.getUserRole();
        const userId = this.loggedInUserService.getLoggedUserId();
        const api = [USER_ROLE.REQUESTER, USER_ROLE.ACCOUNT_MANAGER].includes(userRole)
            ? ApiConstants.CLIENT_SERVICE_LIST.replace('{userId}', userId.toString())
            : this.getServiceListEndPointByRole();

        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getServiceListEndPointByRole(): string {
        if (this.loggedInUserService.isAdminOrSuperAdmin()) {
            return this.handleSectionFilter(ApiConstants.SERVICE_LIST);
        }
        return ApiConstants.SERVICE_LIST;
    }

    public getAdmissionServicesList(): Observable<{}> {
        const list = Object.keys(ADMISSION_REQUEST_TYPE);
        return of({
            data: list.map((s, i) => ({
                id: s,
                name: ADMISSION_REQUEST_TYPE[s]
            }))
        });
    }

    searchUsersForSms(searchString: string) {
        const api = ApiConstants.SMS_SEARCH_USERS;
        let parameters = new HttpParams();
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.ACTIVE, true);
        return this.http.get(api + '?_q=' + searchString, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getServiceListForManager() {
        const api = this.getServiceListForManagerEndPointByRole();
        return this.http.get(api);
    }

    private getServiceListForManagerEndPointByRole(): string {
        if (this.loggedInUserService.isSectionManager() || this.loggedInUserService.isShiftManager() || this.loggedInUserService.isEmployee()) {
            return this.replaceSectionId(ApiConstants.SERVICE_LIST_MANAGER_BY_SECTION);
        }
        if (this.loggedInUserService.isAdminOrSuperAdmin()) {
            return this.handleSectionFilter(ApiConstants.SERVICE_LIST_MANAGER);
        }
        if (this.loggedInUserService.isOperationEmployee()) {
            return this.handleSectionFilter(ApiConstants.SERVICE_LIST);
        }
        return ApiConstants.SERVICE_LIST_MANAGER;
    }

    getRequestsCount() {
        const api = this.handleSectionQueryParam(ApiConstants.REQUESTS_COUNT);
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    getCallRecordCount() {
        const api = this.handleSectionQueryParam(ApiConstants.CALL_RECORD_COUNT);
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    getRequestById(requestId: string, parameters: HttpParams, viewType: VIEW_TYPE) {
        const api = this.getRequestByIdEndPoint(requestId, viewType);
        const userRole = this.loggedInUserService.getUserRole();
        if (userRole === USER_ROLE.OPERATION_SHIFT_MANAGER) {
            parameters = this.paramHandlerService.setIncludePendingWithParam(parameters);
        }
        return this.http.get<RequestDetailsResponse>(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getRequestByIdEndPoint(requestId: string, viewType: VIEW_TYPE): string {
        const userRole = this.loggedInUserService.getUserRole();
        if (this.paramHandlerService.isAdmissionRequestViewType(viewType)) {
            return ApiConstants.ADMISSIONS_FOR_ACCOUNT_MANAGER + '/' + requestId;
        }
        if (userRole === USER_ROLE.REQUESTER || userRole === USER_ROLE.ACCOUNT_MANAGER) {
            return ApiConstants.REQUESTER_REQUEST_INFO + '/' + requestId;
        } else {
            return ApiConstants.EMPLOYEE_REQUEST_INFO + '/' + requestId;
        }
    }

    getReturnedRequestAllComments(requestId: string, page: number, pageSize: number) {
        let parameters = new HttpParams();
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.PAGE, page);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SIZE, pageSize);
        const api = ApiConstants.REQUESTS_LIST + '/' + requestId + '/comments';
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    uploadFilesForRequester(fileObject: File) {
        return this.uploadAttachmentUrlForRequester(fileObject);
    }

    uploadAttachmentUrlForRequester(fileObject: File) {
        const api = ApiConstants.REQUESTER_UPLOAD_URL;
        const formData = new FormData();
        formData.append('file', fileObject, fileObject.name);
        formData.append('reportProgress', 'true');
        return this.http.post(api, formData).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    uploadFilesForReport(reportId: number, fileObject: File, user) {
        return this.uploadAttachmentUrlForReport(reportId, fileObject, user);
    }
    uploadFilesForCallRecord(fileObject: File) {
        const api = ApiConstants.CALL_RECORD_UPLOAD_URL;

        const formData = new FormData();
        formData.append('file', fileObject, fileObject.name);
        formData.append('reportProgress', 'true');
        return this.http.post(api, formData).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    uploadFilesForUserClients(fileObject: File) {
        const api = ApiConstants.USER_CLIENT_UPLOAD_URL;

        const formData = new FormData();
        formData.append('file', fileObject, fileObject.name);
        formData.append('reportProgress', 'true');
        return this.http.post(api, formData).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    uploadAttachmentUrlForReport(reportId: number, fileObject: File, user) {
        let parameters = new HttpParams();
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.REPORT_ATTACHMENT_USER, user);
        const api = ApiConstants.UPLOAD_ATTACHMENTS_REPORT.replace('{reportId}', reportId.toString());
        const formData = new FormData();
        formData.append('file', fileObject, fileObject.name);
        formData.append('reportProgress', 'true');
        return this.http.post(api, formData, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    uploadAttachmentUrlForTicketing(fileObject: File) {
        const api = ApiConstants.TICKETING_UPLOAD_URL;
        const formData = new FormData();
        formData.append('file', fileObject, fileObject.name);
        formData.append('reportProgress', 'true');
        return this.http.post(api, formData).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    uploadFilesForTicketing(fileObject: File) {
        return this.uploadAttachmentUrlForTicketing(fileObject);
    }
    uploadFile(requestId: number, fileObject: File) {
        return this.uploadAttachmentUrlForEmployees(requestId, fileObject);
    }

    uploadAttachmentUrlForEmployees(requestId: number, fileObject: File) {
        const api = ApiConstants.EMPLOYEE_UPLOAD_URL.replace('{requestId}', requestId.toString());
        const formData = new FormData();
        formData.append('file', fileObject, fileObject.name);
        formData.append('reportProgress', 'true');
        return this.http.post(api, formData).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    // uploadFileToObjectStore(fileObject: File, url: string, contentType: string, firstApiResponse) {
    //     if (environment.production) {
    //         url = url.replace(GlobalConstants.PROD_OBJECT_STORE, GlobalConstants.TUWAIQ_HOST);
    //     }
    //     return this.http.put(url, fileObject, { headers: { 'content-type': contentType } }).pipe(
    //         map(response => {
    //             TuwaiqUtil.convertObjectKeysToCamelCase(response);
    //             // return response of first API
    //             return firstApiResponse;
    //         }),
    //         catchError(this.errorHandler.bind(this))
    //     );
    // }

    closeRequest(requestId: number, body) {
        const api = ApiConstants.CLOSE_REQUEST.replace('{requestId}', requestId.toString());
        return this.http.post(api, body).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    compelteRequestOperation(requestId: number, body) {
        const api = ApiConstants.COPMPLETE_REQUEST_OPERATION.replace('{requestId}', requestId.toString());
        const requestBody = {
            comment: body?.comment,
            attachments: body?.attachments
        };
        return this.http.post(api, requestBody).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    returnRequest(requestId: number, body) {
        const api = ApiConstants.RETURN_REQUEST.replace('{requestId}', requestId.toString());
        return this.http.post(api, body).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    returnRequestconfirmed(requestId: number) {
        const api = ApiConstants.RETURN_REQUEST_CONFIRMED.replace('{requestId}', requestId.toString());
        return this.http.patch(api, '').pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    downloadAttachment(moduleName, requestId: number | undefined, attachmentId: number, commentId?: number, viewType?) {
        const idToUse = requestId !== undefined ? requestId : commentId;

        const userRole = this.loggedInUserService.getUserRole();
        let api = '';
        if (viewType === VIEW_TYPE.CALL_CENTER_CLIENT_REQUEST || viewType === VIEW_TYPE.SECURITY_COORDINATOR_REQUESTS) {
            api = ApiConstants.EMPLOYEE_ATTACHMENTS_ADMISSION_SUPER_QUALITY;
            api = api.replace('{requestId}', idToUse.toString());
        } else if (viewType === VIEW_TYPE.EMPLOYEE_MANAGEMENT_REQUESTS) {
            api = ApiConstants.EMPLOYEE_ATTACHMENTS_ADMISSION_ACCOUNT;
            api = api.replace('{requestId}', idToUse.toString());
        } else if (moduleName == 'ticketing') {
            api = ApiConstants.TICKETING_ATTACHMENTS;
            api = api.replace('{ticketId}', idToUse.toString());
        } else if (moduleName == 'report') {
            api = ApiConstants.DOWNLOAD_ATTACHMENTS_REPORT;
            api = api.replace('{reportCommentId}', idToUse.toString());
        } else if (moduleName == 'callRecord') {
            api = ApiConstants.CALL_RECORD_DOWNLOAD_URL;
            api = api.replace('{callRecordId}', idToUse.toString());
        } else {
            if (userRole === USER_ROLE.REQUESTER || userRole === USER_ROLE.ACCOUNT_MANAGER) {
                // client
                api = ApiConstants.CLIENT_ATTACHMENTS;
            } else if (userRole === USER_ROLE.OPERATION_EMPLOYEE || userRole === USER_ROLE.OPERATION_SHIFT_MANAGER) {
                // operations
                api = ApiConstants.OPERATION_ATTACHMENTS;
            } else {
                // employees
                api = ApiConstants.EMPLOYEE_ATTACHMENTS;
            }
            api = api.replace('{requestId}', idToUse.toString());
        }
        api = api.replace('{attachmentId}', attachmentId.toString());
        return this.http.get(api, { responseType: 'blob', observe: 'response' });
    }

    downloadFileFromObjectStore(url: string, contentType) {
        if (environment.production) {
            url = url.replace(GlobalConstants.PROD_OBJECT_STORE, GlobalConstants.TUWAIQ_HOST);
        }
        return this.http.get(url, { responseType: 'blob', observe: 'response' });
    }
    getFilebase64(requestId: number, attachmentId: number, moduleName: string = null, viewType?) {
        const userRole = this.loggedInUserService.getUserRole();

        let api = ApiConstants.EMPLOYEE_ATTACHMENTS_PREVIEW;
        if (viewType === VIEW_TYPE.CALL_CENTER_CLIENT_REQUEST || viewType === VIEW_TYPE.SECURITY_COORDINATOR_REQUESTS) {
            api = ApiConstants.EMPLOYEE_ATTACHMENTS_PREVIEW_ADMISSION_SUPER_QUALITY;
        } else if (viewType === VIEW_TYPE.EMPLOYEE_MANAGEMENT_REQUESTS) {
            api = ApiConstants.EMPLOYEE_ATTACHMENTS_PREVIEW_ADMISSION_ACCOUNT;
        } else if (moduleName == 'ticketing') {
            api = ApiConstants.TICKETING_ATTACHMENTS_PREVIEW;
        } else {
            if ([USER_ROLE.EMPLOYEE, USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN, USER_ROLE.SECTION_MANAGER, USER_ROLE.SHIFT_MANAGER].includes(userRole)) {
                api = ApiConstants.EMPLOYEE_ATTACHMENTS_PREVIEW;
            } else if ([USER_ROLE.OPERATION_SHIFT_MANAGER, USER_ROLE.OPERATION_EMPLOYEE].includes(userRole)) {
                api = ApiConstants.OPERATION_ATTACHMENTS_PREVIEW;
            } else if ([USER_ROLE.REQUESTER, USER_ROLE.ACCOUNT_MANAGER].includes(userRole)) {
                api = ApiConstants.CLIENT_ATTACHMENTS_PREVIEW;
            }
        }
        api = api.replace('{requestId}', requestId.toString());
        api = api.replace('{attachmentId}', attachmentId.toString());
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getSystemList() {
        const api = ApiConstants.SYSTEM_LIST;
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    createRequest(requestBody) {
        const api = ApiConstants.REQUESTS_LIST;
        return this.http.post(api, requestBody).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorListHandler.bind(this))
        );
    }
    downloadAttachmentItem(payLoad) {
        const api = ApiConstants.DOWNLOAD_ATTACHMENTS;
        return this.http.post(api, payLoad, { responseType: 'blob', observe: 'response' });
    }

    dummyLogin(body) {
        const api = ApiConstants.LOGIN;
        return this.http.post(api, body).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getUserDetails(userId: string) {
        const api = ApiConstants.USER_DETAILS.replace('{id}', userId);
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getRequestAverageResponseTime(parameters: HttpParams) {
        const api = this.getRequestsAverageResponseTimeEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    getServiceSlaResponseTime(parameters: HttpParams) {
        const api = ApiConstants.SERVICE_SLA_RESPONSE_TIME;
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    private getRequestsAverageResponseTimeEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.REQUEST_AVERAGE_RESPONSE_TIME_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.REQUEST_AVERAGE_RESPONSE_TIME);
        }
        return ApiConstants.REQUEST_AVERAGE_RESPONSE_TIME;
    }

    getOperationRequestAverageResponseTime(parameters: HttpParams) {
        const api = this.getOperationRequestsAverageResponseTimeEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getOperationRequestsAverageResponseTimeEndPointByRole(): string {
        if (this.loggedInUserService.isAdminOrSuperAdmin()) {
            return this.handleSectionFilter(ApiConstants.OPERATION_REQUEST_AVERAGE_RESPONSE_TIME);
        }
        return ApiConstants.OPERATION_REQUEST_AVERAGE_RESPONSE_TIME;
    }

    getTotalRequestsPerServiceOrSystem(type, parameters: HttpParams) {
        const api = type === 'service' ? this.getRequestsPerServiceEndPointByRole() : this.getRequestsPerSystemEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getRequestsPerServiceEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.REQUESTS_COUNT_PER_SERVICE_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.REQUESTS_COUNT_PER_SERVICE);
        }
        if (this.loggedInUserService.isAccountManager()) {
            return this.handleSectionFilter(ApiConstants.REQUESTS_COUNT_PER_SERVICE_ACCOUNT_MANAGER);
        }
        return ApiConstants.REQUESTS_COUNT_PER_SERVICE;
    }

    private getRequestsPerSystemEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.REQUESTS_COUNT_PER_SYSTEM_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.REQUESTS_COUNT_PER_SYSTEM);
        }
        return ApiConstants.REQUESTS_COUNT_PER_SYSTEM;
    }

    getRequestDailyAnalysis(parameters: HttpParams) {
        const api = this.getRequestDailyAnalysisEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getRequestDailyAnalysisEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.REQUESTS_DAILY_ANALYSIS_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.REQUESTS_DAILY_ANALYSIS);
        }
        if (this.loggedInUserService.isAccountManager()) {
            return this.handleSectionFilter(ApiConstants.REQUESTS_DAILY_ANALYSIS_ACCOUNT_MANAGER);
        }
        return ApiConstants.REQUESTS_DAILY_ANALYSIS;
    }

    getRequestsStatusCount(parameters: HttpParams) {
        const api = this.getRequestsStatusCountEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getRequestsStatusCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.REQUESTS_STATUS_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.REQUESTS_STATUS_COUNT);
        }
        if (this.loggedInUserService.isAccountManager()) {
            return this.handleSectionFilter(ApiConstants.REQUESTS_STATUS_COUNT_ACCOUNT_MANAGER);
        }
        return ApiConstants.REQUESTS_STATUS_COUNT;
    }

    getTotalEmployeeCount() {
        const api = this.getEmployeesCountEndPointByRole();
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getTotalClientCount() {
        const api = this.getClientsCountEndPointByRole();
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getTotalSystemCount() {
        const api = ApiConstants.TOTAL_SYSTEM_COUNT;
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getTotalServiceCount() {
        const api = ApiConstants.TOTAL_SERVICE_COUNT;
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getTotalManagerCount() {
        const api = ApiConstants.TOTAL_MANAGER_COUNT;
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getTotalClientsOrEmployeesCount(type: string) {
        let api;
        switch (type) {
            case DASHBOARD_BLOCK_TYPES.EMPLOYEES:
                api = this.getEmployeesCountEndPointByRole();
                break;
            case DASHBOARD_BLOCK_TYPES.CLIENTS:
                api = this.getClientsCountEndPointByRole();
                break;
            case DASHBOARD_BLOCK_TYPES.OPERATIONS:
                api = this.getOperationsCountEndPointByRole();
                break;

            default:
                api = this.getEmployeesCountEndPointByRole();
                break;
        }
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getEmployeesCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.TOTAL_EMPLOYEE_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.TOTAL_EMPLOYEE_COUNT);
        }
        if (this.loggedInUserService.isAccountManager()) {
            return this.handleSectionFilter(ApiConstants.TOTAL_EMPLOYEE_COUNT_ACCOUNT_MANAGER);
        }
        return ApiConstants.TOTAL_EMPLOYEE_COUNT;
    }

    private getClientsCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.TOTAL_CLIENT_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.TOTAL_CLIENT_COUNT);
        }
        return ApiConstants.TOTAL_CLIENT_COUNT;
    }

    private getOperationsCountEndPointByRole(): string {
        return ApiConstants.TOTAL_OPERATIONS_COUNT;
    }

    exportDashBoardData(parameters: HttpParams, exportEmployeesDashboard?) {
        const url = this.getexportDashBoardDataByRole(exportEmployeesDashboard);
        return this.http.get(url, { params: parameters, responseType: 'blob', observe: 'response' });
    }
    private getexportDashBoardDataByRole(exportEmployeesDashboard?): string {
        if (
            (this.loggedInUserService.isSectionManagerIC() ||
                this.loggedInUserService.isShiftManagerIC() ||
                this.loggedInUserService.isAdminOrSuperAdmin() ||
                this.loggedInUserService.isGeneralManager()) &&
            exportEmployeesDashboard === true
        ) {
            return this.handleSectionFilter(ApiConstants.EXPORT_EXCEL_EMPLOYESS);
        }
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.EXPORT_DASHBOARD_DATA_MANAGER);
        }
        if (this.loggedInUserService.isAccountManager()) {
            return this.replaceSectionId(ApiConstants.EXPORT_DASHBOARD_DATA_ACCOUNT_MANAGER);
        }

        return this.handleSectionFilter(ApiConstants.EXPORT_DASHBOARD_DATA);
    }
    getClientRequestStatusCount(parameters: HttpParams) {
        const api = this.getClientRequestsStatusCountEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getClientRequestsStatusCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.CLIENT_REQUESTS_STATUS_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.CLIENT_REQUESTS_STATUS_COUNT);
        }
        return ApiConstants.CLIENT_REQUESTS_STATUS_COUNT;
    }

    getOperationRequestStatusCount(parameters: HttpParams) {
        const api = this.getOperationRequestsStatusCountEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getOperationRequestsStatusCountEndPointByRole(): string {
        if (this.loggedInUserService.isAdminOrSuperAdmin()) {
            return this.handleSectionFilter(ApiConstants.OPERATION_REQUESTS_STATUS_COUNT);
        }
        return ApiConstants.OPERATION_REQUESTS_STATUS_COUNT;
    }

    getEmployeeRequestStatusCount(parameters: HttpParams) {
        const api = this.getEmployeeRequestStatusCountEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getEmployeeRequestStatusCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.EMPLOYEE_REQUESTS_STATUS_COUNT_BY_SECTION);
        }
        if (this.loggedInUserService.isOperationEmployee()) {
            return this.handleSectionFilter(ApiConstants.OPERATION_EMPLOYEE_REQUESTS_STATUS_COUNT);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.EMPLOYEE_REQUESTS_STATUS_COUNT);
        }
        if (this.loggedInUserService.isAccountManager()) {
            return this.handleSectionFilter(ApiConstants.EMPLOYEE_REQUESTS_STATUS_COUNT_ACCOUNT_MANAGER);
        }
        return ApiConstants.EMPLOYEE_REQUESTS_STATUS_COUNT;
    }

    getRequestStatusCountPerClient(clientId, parameters: HttpParams) {
        const api = this.getRequestStatusCountPerClientEndPointByRole(clientId);
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getRequestStatusCountPerClientEndPointByRole(clientId: string): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.REQUESTS_STATUS_COUNT_PER_CLIENT_BY_SECTION.replace('{clientId}', clientId));
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.REQUESTS_STATUS_COUNT_PER_CLIENT.replace('{clientId}', clientId));
        }
        return ApiConstants.REQUESTS_STATUS_COUNT_PER_CLIENT.replace('{clientId}', clientId);
    }

    getRequestStatusCountPerEmployee(userId, parameters: HttpParams) {
        const api = this.getRequestStatusCountPerEmployeeEndPointByRole(userId);
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getRequestStatusCountPerEmployeeEndPointByRole(userId: string): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.REQUESTS_STATUS_COUNT_EMPLOYEE_BY_SECTION.replace('{userId}', userId));
        }
        if (this.loggedInUserService.isOperationEmployee()) {
            return this.handleSectionFilter(ApiConstants.OPERATION_STATUS_COUNT_EMPLOYEE.replace('{userId}', userId));
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.REQUESTS_STATUS_COUNT_EMPLOYEE.replace('{userId}', userId));
        }
        if (this.loggedInUserService.isAccountManager()) {
            return this.handleSectionFilter(ApiConstants.REQUESTS_STATUS_COUNT_EMPLOYEE_ACCOUNT_MANAGER.replace('{userId}', userId));
        }
        return ApiConstants.REQUESTS_STATUS_COUNT_EMPLOYEE.replace('{userId}', userId);
    }

    getRequestStatusCountPerOperation(userId, parameters: HttpParams) {
        const api = this.getRequestStatusCountPerOperationEndPointByRole(userId);

        if (this.loggedInUserService.isSectionManager()) {
            // add filter section if logged id section manager
            parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SECTION_FILTER, this.loggedInUserService.currentUserSectionId());
        }
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getRequestStatusCountPerOperationEndPointByRole(userId: string): string {
        if (this.loggedInUserService.isAdminOrSuperAdmin()) {
            return this.handleSectionFilter(ApiConstants.OPERATION_STATUS_COUNT_EMPLOYEE.replace('{userId}', userId));
        }
        return ApiConstants.OPERATION_STATUS_COUNT_EMPLOYEE.replace('{userId}', userId);
    }

    updateSystem(systemId: number, newName: string, nameen: string) {
        const modifiedBody = { 'name-ar': newName, 'name-en': nameen };
        const api = ApiConstants.UPDATE_SYSTEM.replace('{systemId}', systemId.toString());
        return this.http.patch(api, modifiedBody).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    addSystem(name: string, nameen: string) {
        const body = { 'name-ar': name, 'name-en': nameen };
        const api = ApiConstants.ADD_SYSTEM;
        return this.http.post(api, body).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    updateService(serviceId: number, newName: string, newenName: string, callCenter?) {
        const modifiedBody = { 'name-ar': newName, 'name-en': newenName, 'call-center': callCenter };
        const api = ApiConstants.UPDATE_SERVICE.replace('{serviceId}', serviceId.toString());
        return this.http.patch(api, modifiedBody).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    updateClient(clientId: number, newName: string, nameen: string, services: [] = []) {
        const modifiedBody = { 'name-ar': newName, 'name-en': nameen, 'service-ids': services };
        const api = ApiConstants.CLIENT.replace('{clientId}', clientId.toString());
        return this.http.patch(api, modifiedBody).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    updateClientManagement(clientId: number, payload: any) {
        const api = ApiConstants.UPDATE_CLIENT.replace('{clientId}', clientId.toString());
        return this.http.put(api, payload).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    addClient(name: string, nameen: string, services: [] = []) {
        const body = { 'name-ar': name, 'name-en': nameen, 'service-ids': services };
        const api = ApiConstants.ADD_CLIENT;
        return this.http.post(api, body).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    addClientManagementList(name: string, nameen: string, departmentId: number, sectionId: number, NewClientUsers) {
        const body = {
            'client-name-ar': name,
            'client-name': nameen,
            'department-id': departmentId,
            'section-id': sectionId,
            'new-client-users': NewClientUsers
        };
        const api = ApiConstants.ADD_CLIENT_MANAGEMENT_LIST;
        return this.http.post(api, body).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    createNewFolder(metadata) {
        const api = ApiConstants.CREATE_NEW_FOLDER;
        const formData = new FormData();
        formData.append('metadata', JSON.stringify(metadata));

        return this.http.post(api, formData).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    shareStorageItems(metadata) {
        const api = ApiConstants.SHARE_STORAGE_ITEMS;

        return this.http.post(api, metadata).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    uploadFileDetails(metadata, file) {
        const api = ApiConstants.CREATE_NEW_FOLDER;
        const formData = new FormData();
        formData.append('metadata', JSON.stringify(metadata));
        formData.append('file', file);

        return this.http.post(api, formData).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getUsersByRole(roleName: USER_ROLE, filter?: any) {
        let parameters = new HttpParams();
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.ROLE_NAME_FILTER, roleName);
        if (filter != undefined) {
            parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.ROLE_STATUS_FILTER, filter);
        }

        const api = this.handleSectionFilter(ApiConstants.USERS);
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getUsersPerClient(clientId, additionalParams?: HttpParams) {
        let parameters = new HttpParams();
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.CLIENT_FILTER, clientId);

        if (additionalParams) {
            additionalParams.keys().forEach(key => {
                parameters = parameters.append(key, additionalParams.get(key));
            });
        }

        const api = ApiConstants.USERS;
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                // TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getClientDetails(clientId: number) {
        const api = ApiConstants.CLIENT.replace('{clientId}', clientId.toString());
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getQAClientUsers(clientId: number, searchQuery?: string): Observable<any> {
        let parameters = new HttpParams();

        if (searchQuery && searchQuery.trim()) {
            parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, searchQuery.trim());
        }

        const api = ApiConstants.QA_CLIENT_USERS.replace('{clientId}', clientId.toString());

        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getClientUserDetails(clientId: number) {
        const api = ApiConstants.GET_SINGLE_CLIENT.replace('{clientId}', clientId.toString());
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    findUser(params: string) {
        let api = ApiConstants.USERS + '/clientless/search';
        let parameters = new HttpParams();
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.PAGE, 1);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SIZE, 30);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, params);

        return this.http.get(api, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                //if 204 status, indicating there is no user profiles, return null
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    findUserByRole(params: string) {
        let api = ApiConstants.SEARCH_USERS_CLIENT_MANAGEMENT;
        let parameters = new HttpParams();
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.PAGE, 1);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SIZE, 30);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, params);
        // parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.ROLE_NAME_FILTER, 'EServiceClientRole');

        return this.http.get(api, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                //if 204 status, indicating there is no user profiles, return null
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    findUserByServiceAccounts(params: string) {
        let api = ApiConstants.SEARCH_USERS_SERVICE_ACCOUNT;
        let parameters = new HttpParams();

        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, params);

        return this.http.get(api, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                //if 204 status, indicating there is no user profiles, return null
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    searchUser(params: string) {
        let api = ApiConstants.SEARCH_USER;

        const body = {
            page: 1,
            size: 100,
            keyword: params
        };

        return this.http.post(api, body, { observe: 'response' }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                //if 204 status, indicating there is no user profiles, return null
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    findTags(params: string) {
        let api = ApiConstants.TAGS;
        let parameters = new HttpParams();
        const body = {
            [TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_KEYWORD]: params
        };

        return this.http.post(api, body, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let tags = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                if (response.status === 204) {
                    return (tags = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    findUserAccountManager(params: string, clientId: number) {
        let api = `${ApiConstants.USERS}/clientless/or/${clientId}/search`;
        let parameters = new HttpParams();
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.PAGE, 1);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SIZE, 30);
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, params);

        return this.http.get(api, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                //if 204 status, indicating there is no user profiles, return null
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    findUserSTS(params: string, excludeRole?) {
        let api = ApiConstants.USERS + '/sts/search';
        let parameters = new HttpParams();

        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, params);
        if (excludeRole != undefined) parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.EXCLUDE_ROLE_NAME, excludeRole);

        return this.http.get(api, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                //if 204 status, indicating there is no user profiles, return null
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    findUsersToPromote(params: string, excludeRole?) {
        let api = ApiConstants.USERS + '/managers/search';
        let parameters = new HttpParams();

        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, params);
        if (excludeRole != undefined) parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.EXCLUDE_ROLE_NAME, excludeRole);

        return this.http.get(api, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    findUsersBySection(sectionId, querySearch) {
        let api = `${ApiConstants.All_SECTIONS}/${sectionId}/users`;
        let parameters = new HttpParams();

        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, querySearch);

        return this.http.get(api, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    findUsersByDepartment(departmentId, querySearch) {
        const api = ApiConstants.GET_USER_BY_DEPARTMENT.replace('{departmentId}', departmentId.toString());

        let parameters = new HttpParams();

        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, querySearch);

        return this.http.get(api, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    findUsersStakHoldersBySearch(querySearch) {
        let api = ApiConstants.STAKEHOLDERS_USERS;
        let parameters = new HttpParams();

        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, querySearch);

        return this.http.get(api, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                //if 204 status, indicating there is no user profiles, return null
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    findUserIC(params: string) {
        let api = ApiConstants.USERS + '/ic/search';
        let parameters = new HttpParams();

        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.SEARCH_QUERY, params);

        return this.http.get(api, { observe: 'response', params: parameters }).pipe(
            map((response: any) => {
                let userProfiles = response.body;
                if (response.status === 200) {
                    TuwaiqUtil.convertObjectKeysToCamelCase(response);
                    return response.body.data;
                }
                //if 204 status, indicating there is no user profiles, return null
                if (response.status === 204) {
                    return (userProfiles = null);
                }
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    addUser(body) {
        const api = ApiConstants.USERS;
        return this.http.post(api, body).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    addUserAccountManagers(email, clientId) {
        let api = ApiConstants.USERS_ACCOUNT_MANAGERS;
        api = api.replace('{email}', email);
        api = api.replace('{clientId}', clientId);
        return this.http.patch(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    addUserIC(value) {
        const api = ApiConstants.USERS_IC.replace('{email}', value.email);
        return this.http.patch(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    addUserSTC(value) {
        const api = ApiConstants.USERS_STC.replace('{email}', value.email);
        return this.http.patch(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    addUserOperationEmployee(value) {
        const api = ApiConstants.USERS_OPERTAION_EMPLOYEE.replace('{email}', value.email);
        return this.http.patch(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    addUserOperationManager(value) {
        const api = ApiConstants.USERS_OPERTAION_MANAGER.replace('{email}', value.email);
        return this.http.patch(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    addUserForTheEntities(clientId: string, email: string) {
        let api = ApiConstants.CLIENT + '/users/requesters/{email}';
        api = api.replace('{clientId}', clientId);
        api = api.replace('{email}', email);
        return this.http.patch(api, {}).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    addUserToClient(value) {
        const body = { 'new-client-users': value.clientUsers };
        let api = ApiConstants.ADD_USER_TO_CLIENT;
        api = api.replace('{clientId}', value.clientId);
        return this.http.put(api, body).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    addUserPromote(value) {
        let api;
        if (value['role-name'] === USER_ROLE.SECTION_MANAGER) {
            api = ApiConstants.USERS_PROMOTE_SECTION_MANAGER.replace('{email}', value.email);
        }
        if (value['role-name'] === USER_ROLE.SHIFT_MANAGER) {
            api = ApiConstants.USERS_PROMOTE_SHIFT_MANAGER.replace('{email}', value.email);
        }
        if (value['role-name'] === USER_ROLE.ADMIN) {
            api = ApiConstants.USERS_PROMOTE_ADMIN.replace('{email}', value.email);
        }

        return this.http.patch(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    demoteUser(email: string) {
        const api = `${ApiConstants.USERS}/${email}/demote`;
        return this.http.patch(api, {}).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    demoteUserOperation(email: string) {
        const api = ApiConstants.DEMOTE_USER_OPERATION.replace('{email}', email);
        return this.http.patch(api, {}).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    deleteRequester(email: string) {
        const api = `${ApiConstants.USERS}/requesters/${email}/deactivate`;
        return this.http.patch(api, {}).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getClientListWithUserCount() {
        let parameters = new HttpParams();
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.INCLUDE, TuwaiqConfig.REQUEST_INCLUDES.USERS_COUNT);
        const api = this.getClientListEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    searchClientsByName(searchTerm: string) {
        let parameters = new HttpParams();
        parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.INCLUDE, TuwaiqConfig.REQUEST_INCLUDES.USERS_COUNT);
        if (searchTerm && searchTerm.trim() !== '') {
            parameters = parameters.append('name', searchTerm.trim());
        }

        const api = this.getClientListEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getClientManagementList(parameters?: HttpParams) {
        const api = this.getClientManagementListEndPointByRole();
        return this.http.get(api, { params: parameters, observe: 'response' }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getempolyeeMangement(filter?: any, empolyeeMangmentOpertation?: boolean) {
        let parameters = new HttpParams();
        if (filter != undefined) {
            parameters = parameters.append(TuwaiqConfig.REQUEST_QUERY_PARAMS.ROLE_STATUS_FILTER, filter);
        }
        const api = this.getempolyeeMangementByRole(empolyeeMangmentOpertation);
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getempolyeeMangementByRole(empolyeeMangmentOpertation?: boolean): string {
        if (this.loggedInUserService.isSectionManager() || this.loggedInUserService.isShiftManager()) {
            return this.replaceSectionId(ApiConstants.EMPOLYEE_MANAGMNET_SECTION_MANAGER);
        }
        if (this.loggedInUserService.isOperationShiftManager()) {
            return ApiConstants.EMPOLYEE_MANAGMNET_OPERATIONS;
        }
        if (this.loggedInUserService.isAdminOrSuperAdmin()) {
            if (empolyeeMangmentOpertation == true && empolyeeMangmentOpertation != undefined) {
                return ApiConstants.EMPOLYEE_MANAGMNET_OPERATIONS;
            } else {
                return this.handleSectionFilter(ApiConstants.EMPOLYEE_MANAGMNET);
            }
        }
        return ApiConstants.REQUEST_AVERAGE_RESPONSE_TIME;
    }
    getUsersClientByClientId(value, additionalParams?: HttpParams) {
        const api = ApiConstants.USERS_CLIENT.replace('{clientId}', value);

        // Prepare request options
        const requestOptions: any = {};

        // Add parameters if provided
        if (additionalParams) {
            requestOptions.params = additionalParams;
        }

        return this.http.get(api, requestOptions).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    empolyeeMangementAvailable(value) {
        const api = ApiConstants.EMPOLYEE_MANAGMNET_AVAILABLE_STATUS.replace('{email}', value.email);
        return this.http.patch(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    empolyeeAvailable() {
        const api = ApiConstants.EMPOLYEE_AVAILABLE_STATUS;
        return this.http.patch(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    empolyeeManagementUNAvailable(value) {
        const api = ApiConstants.EMPOLYEE_MANAGMNET_UNAVAILABLE_STATUS.replace('{email}', value.email);
        return this.http.patch(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    empolyeeUNAvailable() {
        const api = ApiConstants.EMPOLYEE_UNAVAILABLE_STATUS;
        return this.http.patch(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    assignRequestToOperation(requestIDs) {
        let api;
        const requestBody = {
            'request-ids': requestIDs
        };

        api = ApiConstants.SEND_MULTIPLE_REQUESTS_TO_OPERATION;

        return this.http.post(api, requestBody).pipe(
            map(response => {
                //since response is null,
                response = response === null ? 'success' : response;
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getAssignValue() {
        const api = ApiConstants.ASSIGN_AUTOMATIC_VALUE_BY_SECTION.replace('{sectionId}', this.loggedInUserService.currentUserSectionId());
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    assignAutomatic(autoAssignValue) {
        const api = ApiConstants.ASSIGN_AUTOMATIC.replace('{autoAssignValue}', autoAssignValue);
        return this.http.put(api, null).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    /** Error Handling method */
    errorHandler(errorResponse: HttpErrorResponse) {
        if (errorResponse.status === 401) {
            // this.authService.logout()
            this.handleSessionExpired();
        } else if (errorResponse.error && errorResponse.error['errors']) {
            return throwError(() => errorResponse.error['errors'][0]);
        }
        return throwError(() => 'An error occurred; please try again later.');
    }

    //TEMP:
    errorListHandler(errorResponse: HttpErrorResponse) {
        if (errorResponse.status === 401) {
            // this.authService.logout()
            this.handleSessionExpired();
        } else if (errorResponse.error && errorResponse.error['errors']) {
            return throwError(() => errorResponse.error['errors']);
        }
        return throwError(() => 'An error occurred; please try again later.');
    }

    handleSessionExpired() {
        if (!this.isExpiredSession) {
            this.dialog.open(SessionExpiredComponent, {
                width: '412px',
                disableClose: true
            });
            this.isExpiredSession = true;
        }
    }

    private replaceSectionId(url: string) {
        return url.replace('{sectionId}', this.loggedInUserService.currentUserSectionId());
    }

    private handleSectionQueryParam(url: string): string {
        if (
            (this.loggedInUserService.isAdminOrSuperAdmin() ||
                this.loggedInUserService.isGeneralManager() ||
                this.loggedInUserService.IsUserRelatedQualityAssurance()) &&
            this.loggedInUserService.viewAsSection()
        ) {
            const link = new URL(url);
            link.searchParams.set(TuwaiqConfig.REQUEST_QUERY_PARAMS.SECTION_ID, this.loggedInUserService.viewAsSection());
            return link.toString();
        }
        return url;
    }

    private handleSectionFilter(url: string): string {
        if (
            ((this.loggedInUserService.isAdminOrSuperAdmin() ||
                this.loggedInUserService.isGeneralManager() ||
                this.loggedInUserService.IsUserRelatedQualityAssurance() ||
                this.loggedInUserService.isSectionManagerIC()) &&
                this.loggedInUserService.viewAsSection()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            const link = new URL(url);
            if (this.loggedInUserService.isShiftManagerIC()) {
                link.searchParams.set(TuwaiqConfig.REQUEST_QUERY_PARAMS.SECTION_FILTER, this.loggedInUserService.currentUserSectionId());
            } else {
                link.searchParams.set(TuwaiqConfig.REQUEST_QUERY_PARAMS.SECTION_FILTER, this.loggedInUserService.viewAsSection());
            }
            return link.toString();
        }
        return url;
    }

    private isAdmissionRequestViewType(viewType: VIEW_TYPE): boolean {
        return [VIEW_TYPE.EMPLOYEE_MANAGEMENT_REQUESTS, VIEW_TYPE.SECURITY_COORDINATOR_REQUESTS].includes(viewType);
    }

    //  Call Record Dashboard Functions

    getCallRecordTotalClientsOrEmployeesCount(type: string) {
        let api;
        switch (type) {
            case CALL_RECORD_DASHBOARD_BLOCK_TYPES.EMPLOYEES:
                api = this.getCallRecordEmployeesCountEndPointByRole();
                break;
            case CALL_RECORD_DASHBOARD_BLOCK_TYPES.CLIENTS:
                api = this.getCallRecordClientsCountEndPointByRole();
                break;

            default:
                api = this.getCallRecordEmployeesCountEndPointByRole();
                break;
        }
        return this.http.get(api).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    private getCallRecordEmployeesCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            console.log('true');
            return this.replaceSectionId(ApiConstants.TOTAL_CALL_RECORD_EMPLOYEE_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            console.log('false');
            return this.handleSectionFilter(ApiConstants.TOTAL_CALL_RECORD_EMPLOYEE_COUNT);
        }
        // if (this.loggedInUserService.isAccountManager()) {
        //     return this.handleSectionFilter(ApiConstants.TOTAL_CALL_RECORD_EMPLOYEE_COUNT_ACCOUNT_MANAGER);
        // }
        return ApiConstants.TOTAL_CALL_RECORD_EMPLOYEE_COUNT;
    }

    private getCallRecordClientsCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.TOTAL_CALL_RECORD_CLIENT_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.TOTAL_CALL_RECORD_CLIENT_COUNT);
        }
        return ApiConstants.TOTAL_CALL_RECORD_CLIENT_COUNT;
    }

    private getCallRecordAllCallsCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.TOTAL_CALL_RECORD_ALL_CALLS_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.TOTAL_CALL_RECORD_ALL_CALLS_COUNT);
        }
        return ApiConstants.TOTAL_CALL_RECORD_ALL_CALLS_COUNT;
    }
    private getCallRecordCancelledCallsCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.TOTAL_CALL_RECORD_CANCELED_CALLS_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.TOTAL_CALL_RECORD_CANCELED_CALLS_COUNT);
        }
        return ApiConstants.TOTAL_CALL_RECORD_CANCELED_CALLS_COUNT;
    }
    private getCallRecordClosedCallsCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.TOTAL_CALL_RECORD_CLOSED_CALLS_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.TOTAL_CALL_RECORD_CLOSED_CALLS_COUNT);
        }
        return ApiConstants.TOTAL_CALL_RECORD_CLOSED_CALLS_COUNT;
    }
    getTotalCallRecordPerServiceOrSystem(type, parameters: HttpParams) {
        const api = type === 'service' ? this.getCallRecordPerServiceEndPointByRole() : this.getCallRecordPerSystemEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getCallRecordPerServiceEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.CALL_RECORD_COUNT_PER_SERVICE_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.CALL_RECORD_COUNT_PER_SERVICE);
        }
        // if (this.loggedInUserService.isAccountManager()) {
        //     return this.handleSectionFilter(ApiConstants.REQUESTS_COUNT_PER_SERVICE_ACCOUNT_MANAGER);
        // }
        return ApiConstants.CALL_RECORD_COUNT_PER_SERVICE;
    }
    private getCallRecordPerSystemEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.CALL_RECORD_COUNT_PER_SYSTEM_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.CALL_RECORD_COUNT_PER_SYSTEM);
        }
        return ApiConstants.CALL_RECORD_COUNT_PER_SYSTEM;
    }
    getCallRecordDailyAnalysis(parameters: HttpParams) {
        const api = this.getCallRecordDailyAnalysisEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getCallRecordDailyAnalysisEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.CALLS_RECORD_DAILY_ANALYSIS_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.CALLS_RECORD_DAILY_ANALYSIS);
        }
        // if (this.loggedInUserService.isAccountManager()) {
        //     return this.handleSectionFilter(ApiConstants.REQUESTS_DAILY_ANALYSIS_ACCOUNT_MANAGER);
        // }
        return ApiConstants.CALLS_RECORD_DAILY_ANALYSIS;
    }
    getCallsStatusCount(parameters: HttpParams) {
        const api = this.getCallsStatusCountEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    private getCallsStatusCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.CALLS_STATUS_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.CALLS_STATUS_COUNT);
        }
        // if (this.loggedInUserService.isAccountManager()) {
        //     return this.handleSectionFilter(ApiConstants.REQUESTS_STATUS_COUNT_ACCOUNT_MANAGER);
        // }
        return ApiConstants.CALLS_STATUS_COUNT;
    }
    exportCallsDashBoardData(parameters, exportEmployeesDashboard?) {
        const url = this.getExportCallsDashBoardDataByRole(exportEmployeesDashboard);
        return this.http.get(url, { params: parameters, responseType: 'blob', observe: 'response' });
    }
    private getExportCallsDashBoardDataByRole(exportEmployeesDashboard?): string {
        if (
            (this.loggedInUserService.isSectionManagerIC() ||
                this.loggedInUserService.isShiftManagerIC() ||
                this.loggedInUserService.isAdminOrSuperAdmin() ||
                this.loggedInUserService.isGeneralManager()) &&
            exportEmployeesDashboard === true
        ) {
            return this.handleSectionFilter(ApiConstants.EXPORT_EXCEL_EMPLOYESS);
        }
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.EXPORT_CALLS_DASHBOARD_DATA_MANAGER);
        }

        // if (this.loggedInUserService.isAccountManager()) {
        //     return this.replaceSectionId(ApiConstants.EXPORT_DASHBOARD_DATA_ACCOUNT_MANAGER);
        // }
        return this.handleSectionFilter(ApiConstants.EXPORT_CALLS_DASHBOARD_DATA);
    }
    getClientCallsStatusCount(parameters: HttpParams) {
        const api = this.getClientCallsStatusCountEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    getClientCallsStatusCountEndPointByRole() {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.CLIENT_CALLS_STATUS_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.CLIENT_CALLS_STATUS_COUNT);
        }
        return ApiConstants.CLIENT_CALLS_STATUS_COUNT;
    }
    getEmployeeCallsStatusCount(parameters: HttpParams) {
        const api = this.getEmployeeCallsStatusCountEndPointByRole();
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    private getEmployeeCallsStatusCountEndPointByRole(): string {
        if (
            (this.loggedInUserService.isSectionManager() && !this.loggedInUserService.IsUserRelatedQualityAssurance()) ||
            this.loggedInUserService.isShiftManagerIC()
        ) {
            return this.replaceSectionId(ApiConstants.EMPLOYEE_CALLS_STATUS_COUNT_BY_SECTION);
        }
        if (
            this.loggedInUserService.isAdminOrSuperAdmin() ||
            this.loggedInUserService.isGeneralManager() ||
            this.loggedInUserService.IsUserRelatedQualityAssurance()
        ) {
            return this.handleSectionFilter(ApiConstants.EMPLOYEE_CALLS_STATUS_COUNT);
        }

        return ApiConstants.EMPLOYEE_CALLS_STATUS_COUNT;
    }
    getCallsStatusCountPerClient(clientId, parameters: HttpParams) {
        const api = this.getCallsStatusCountPerClientEndPointByRole(clientId);
        return this.http.get(api, { params: parameters }).pipe(
            map(response => {
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    private getCallsStatusCountPerClientEndPointByRole(clientId: string): string {
        if (this.loggedInUserService.isSectionManager()) {
            return this.replaceSectionId(ApiConstants.CALLS_STATUS_COUNT_PER_CLIENT_BY_SECTION.replace('{clientId}', clientId));
        }
        if (this.loggedInUserService.isAdminOrSuperAdmin() || this.loggedInUserService.isGeneralManager()) {
            return this.handleSectionFilter(ApiConstants.CALLS_STATUS_COUNT_PER_CLIENT.replace('{clientId}', clientId));
        }
        return ApiConstants.CALLS_STATUS_COUNT_PER_CLIENT.replace('{clientId}', clientId);
    }
    exportClients() {
        // Get the user's timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        let api = `${ApiConstants.EXPORT_EXCEL_CLIENTS}?timezone=${encodeURIComponent(timezone)}`;
        return this.http.get(api, { responseType: 'blob', observe: 'response' });
    }
    exportClientById(parameters) {
        let api = ApiConstants.EXPORT_EXCEL_CLIENTS;
        return this.http.get(api, { params: parameters, responseType: 'blob', observe: 'response' });
    }
}
